import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import useDocTitle from '../utils/useDocTitle';
import axios from 'axios';
import DOMPurify from 'dompurify'; // Import DOMPurify for sanitizing HTML content
import '../index.css';

const Annex = () => {
  const { language } = useLanguage(); // Language context
  useDocTitle(language === 'English' ? 'What is ANNEX?' : 'Çfarë është ANNEX?');

  const [mainData, setMainData] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMainData = async () => {
      try {
        const response = await axios.get('https://annexpilot.com/dashboard/wp-json/wp/v2/homepage');
        const filteredData = response.data.find((item) => item.page_language[0].name === language);
        if (filteredData) {
          setMainData({
            mainTitle:
              filteredData.main_title ||
              (language === 'English' ? 'What is ANNEX?' : 'ÇFARË ËSHTË ANNEX?'),
            mainText: filteredData.main_text || '',
            mainImage: filteredData.main_image.guid || '/images/main/default.jpg',
            imageAlt:
              filteredData.main_image.post_excerpt ||
              (language === 'English' ? 'Default Annex Image' : 'Imazhi i paracaktuar i ANNEX'),
          });
        } else {
          setMainData(null);
        }
      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMainData();
  }, [language]);

  const handleToggleShowMore = () => setShowMore((prev) => !prev);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        {language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}
      </div>
    );
  }

  if (!mainData) {
    return (
      <div className="flex items-center justify-center h-screen">
        {language === 'English' ? 'Content not available.' : 'Përmbajtja nuk është e disponueshme.'}
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <header
        className="relative w-full overflow-hidden"
        style={{
          paddingTop: '5rem',
          backgroundImage: `url(${mainData.mainImage})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          minHeight: '650px',
        }}
        role="img"
        aria-label={mainData.imageAlt}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-60"></div>
        <section className="container-inner relative z-10 flex flex-col items-center lg:flex-row justify-between lg:text-left py-8 px-4 md:px-8">
          <div className="lg:w-3/4 flex flex-col justify-center">
            <h1
              className="text-white text-xl md:text-3xl lg:text-5xl font-bold mb-4 text-center lg:text-left"
              id="annex-header"
              tabIndex="0"
            >
            {language === 'English' ? 'WHAT IS ANNEX?' : 'ÇFARË ËSHTË ANNEX?'}
            </h1>
            <div
              className={`text-white font-bold tracking-tight mb-5 ${
                showMore ? 'w-full' : 'w-full md:w-3/4'
              } text-sm md:text-lg lg:text-xl text-center lg:text-left`}
              aria-labelledby="annex-header"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  showMore ? mainData.mainText : `${mainData.mainText.slice(0, 200)}...`
                ),
              }}
            ></div>
            <button
              onClick={handleToggleShowMore}
              className="btn-custom mt-2 self-center lg:self-start"
              aria-expanded={showMore}
              aria-controls="additional-content"
            >
              {showMore
                ? language === 'English'
                  ? 'Show Less'
                  : 'Shfaq më pak'
                : language === 'English'
                ? 'Show More'
                : 'Shfaq më shumë'}
            </button>

            {/* Links Section */}
            <div className="flex flex-col md:flex-row md:space-x-2 mt-4 items-center lg:items-start">
              <Link
                to="/artistic-program"
                className="btn-custom mb-3 md:mb-0 w-full md:w-auto text-center"
                aria-label={
                  language === 'English'
                    ? 'Learn more about Artistic Programs'
                    : 'Mëso më shumë rreth Programeve Artistike'
                }
              >
                {language === 'English' ? 'Artistic Programs' : 'Programet Artistike'}
              </Link>
              <Link
                to="/educational-program"
                className="btn-custom w-full md:w-auto text-center"
                aria-label={
                  language === 'English'
                    ? 'Learn more about Educational Programs'
                    : 'Mëso më shumë rreth Programeve Edukative'
                }
              >
                {language === 'English' ? 'Educational Programs' : 'Programet Edukative'}
              </Link>
            </div>
          </div>
        </section>
      </header>
    </>
  );
};

export default Annex;